import styled from '@emotion/styled';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { AccidentIncuranceVariant, BaggageIncuranceVariant, convertTravelDestinationBack, DisplayDateFormat2, getBrandLogo, i18n, TravelDestinationEnumLabel } from 'src/common';
import { GetOfferDto, SoTravelTravelData } from 'src/common/apiGraphql/types/types';
import { CardWithTitle, Flex, Text } from 'src/components';
import { colors } from 'src/theme';

interface OfferDetailsCardProps {
  offer: GetOfferDto;
  travelData: SoTravelTravelData;
}

const BrandLogo = styled.img`
  height: 37px;
`;

export const OfferDetailsCard: React.FC<OfferDetailsCardProps> = ({
  offer,
  travelData
}) => {
  const insuranceDuration = useMemo(() => {
    if (!travelData?.insuranceStartDate) return '';
    
    const formatDate = (dateString: string) => {
      return dayjs(dateString).format(DisplayDateFormat2);
    };

    const startDate = formatDate(travelData.insuranceStartDate);
    
    if (travelData?.insuranceEndDate) {
      const endDate = formatDate(travelData.insuranceEndDate);
      return `${startDate}-${endDate}`;
    }
    
    return startDate;
  }, [travelData]);

  const insuranceType = useMemo(() => {
    if (travelData.allInsuredPersonsAreFromTheSameFamily && travelData.numOfInsuredPersons > 1 ) {
      return `${i18n.t("components.cards.offerDetailsCard.family")}(${travelData.numOfInsuredPersons})`;
    }
    return travelData.numOfInsuredPersons > 1 ? `${i18n.t("components.cards.offerDetailsCard.group")}(${travelData.numOfInsuredPersons})` : i18n.t("components.cards.offerDetailsCard.single");
  }, [travelData]);

  const destinations = useMemo(() => {
    return TravelDestinationEnumLabel[convertTravelDestinationBack(travelData.travelDestination)];
  }, [travelData]);

  const additionalCoverages = useMemo(() => {
    return offer.productTypeSpecific.includedCoverages.filter(coverage => !coverage.disabled).map((coverage) => {
      switch (coverage.code) {
        case 'travelHealthInsurance':
          return null;
        case 'accidentInsurance':
          return `${coverage.codeDescription}-${AccidentIncuranceVariant[coverage.variant as keyof typeof AccidentIncuranceVariant]}`;
        case 'baggageInsurance':
            return `${coverage.codeDescription}-${BaggageIncuranceVariant[coverage.variant as keyof typeof BaggageIncuranceVariant]}`;
        default:
          return coverage.codeDescription;
      }
    });
  }, [offer]);

  const insuranceAmount = useMemo(() => {
    return offer.productTypeSpecific.includedCoverages.find(coverage => coverage.code === 'travelHealthInsurance')?.codeDescription.replace('Putno osiguranje - ', '');
  }, [offer]);

  return (
    <CardWithTitle title={i18n.t("components.cards.offerDetailsCard.travelInsurance")} padding={16}>
    <Flex vertical gap={8}>
      <Flex justify="space-between" align="center" gap={16}>
        <div>
          <BrandLogo src={getBrandLogo(offer.product.brand.code)} alt={offer.product.brand.name || offer.product.brand.code} />
        </div>
        <Text type="xLarge" bold>
          {offer.productTypeSpecific.price.totalPrice.toFixed(2)}€
        </Text>
      </Flex>
      <Flex gap={16} justify='space-between'>
        <Text type="small" color={colors.text3}>{i18n.t("components.cards.offerDetailsCard.insuranceAmount")}</Text>
        <Text type="small" color={colors.text3}>{insuranceAmount}</Text>
      </Flex>
      <Flex gap={16} justify='space-between'>
        <Text type="small" color={colors.text3}>{i18n.t("components.cards.offerDetailsCard.insuranceDuration")}</Text>
        <Text type="small" color={colors.text3}>{insuranceDuration}</Text>
      </Flex>
      <Flex gap={16} justify='space-between'>
        <Text type="small" color={colors.text3}>{i18n.t("components.cards.offerDetailsCard.insuranceType")}</Text>
        <Text type="small" color={colors.text3}>{insuranceType}</Text>
      </Flex>
      <Flex gap={16} justify='space-between'>
        <Text type="small" color={colors.text3}>{i18n.t("components.cards.offerDetailsCard.destinations")}</Text>
        <Text type="small" color={colors.text3}>{destinations}</Text>
      </Flex>
      <Flex gap={16} justify='space-between'>
        <Text type="small" color={colors.text3}>{i18n.t("components.cards.offerDetailsCard.additionalCoverages")}</Text>
        <Flex vertical gap={8} justify='flex-end' align='flex-end'>
          {additionalCoverages.filter(coverage => coverage !== null).map((coverage, index) => (
            <Text key={index} type="small" color={colors.text3}>{coverage}</Text>
          ))}
        </Flex>
      </Flex>
    </Flex>
  </CardWithTitle>
  );
};
